import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Navbar, 
  Nav, 
  Dropdown,
  Offcanvas,
  Button,
  Collapse
} from 'react-bootstrap';
import {
  Menu,
  Home,
  Building,
  Users,
  Shield,
  Package,
  ShoppingCart,
  BarChart3,
  Settings,
  Bell,
  LogOut,
  User,
  ChevronDown,
  ChevronRight,
  Search,
  Phone,
  Bot,
  GitBranch,
  MessageSquare,
  Zap,
  Database,
  Brain,
  Workflow,
  Server
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import type { Module } from '../types';

const MainLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, company, logout, isSuperAdmin, modules } = useAuth();
  const [showSidebar, setShowSidebar] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  // Mapeo de iconos
  const iconMap: { [key: string]: React.ElementType } = {
    Home,
    Building,
    Users,
    Shield,
    Package,
    ShoppingCart,
    BarChart: BarChart3,
    Settings,
    Phone,
    Bot,
    GitBranch,
    MessageSquare,
    Zap,
    Database,
    Brain,
    Workflow,
    Server
  };

  // Configuración del submenú de sistema
  const systemConfigSubmenu = [
    { id: 'db', name: 'Base de Datos', route: '/admin/config/database', icon: 'Database' },
    { id: 'twilio', name: 'Twilio APIs', route: '/admin/config/twilio', icon: 'MessageSquare' },
    { id: 'ai', name: 'Inteligencia Artificial', route: '/admin/config/ai', icon: 'Brain' },
    { id: 'automation', name: 'Automatización (n8n)', route: '/admin/config/automation', icon: 'Workflow' }
  ];

  const getIcon = (iconName: string) => {
    const Icon = iconMap[iconName] || Home;
    return <Icon size={20} />;
  };

  const isActiveRoute = (route: string) => {
    return location.pathname === route || location.pathname.startsWith(route + '/');
  };

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="d-flex">
      {/* Sidebar Desktop */}
      <div className={`sidebar ${showSidebar ? 'collapsed' : ''}`} 
           style={{ 
             width: showSidebar ? '70px' : '250px',
             minHeight: '100vh',
             transition: 'all 0.3s',
             position: 'fixed',
             left: 0,
             top: 0,
             zIndex: 1000,
             backgroundColor: '#ffffff',
             borderRight: '1px solid #e5e7eb',
             boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
           }}>
        <div className="sidebar-header p-3 border-bottom" style={{ borderColor: '#e5e7eb' }}>
          <div className="d-flex align-items-center justify-content-between">
            {!showSidebar && (
              <div className="d-flex align-items-center">
                <img 
                  src="https://cdn.prod.website-files.com/6296605b1f3f0029f029976a/64d3e0b2e9b27088a519f38a_Recurso%201.png"
                  alt="TotalDealer"
                  style={{ width: '30px', height: '30px', marginRight: '10px' }}
                />
                <h5 className="mb-0 text-primary">TotalDealer</h5>
              </div>
            )}
            <Button
              variant="link"
              className="p-0"
              style={{ color: '#6b7280' }}
              onClick={() => setShowSidebar(!showSidebar)}
            >
              <Menu size={24} />
            </Button>
          </div>
          {!showSidebar && company && (
            <small className="d-block mt-2" style={{ color: '#9ca3af' }}>{company.tradeName || company.name}</small>
          )}
        </div>

        <Nav className="flex-column p-3">
          {modules.map((module: Module) => {
            // Check if this is the System Config module
            if (module.route === '/admin/config') {
              return (
                <div key={module.id}>
                  <Nav.Link
                    className={`mb-2 rounded d-flex align-items-center justify-content-between ${isActiveRoute(module.route) ? 'text-white' : ''}`}
                    style={{ 
                      padding: '10px',
                      textDecoration: 'none',
                      color: isActiveRoute(module.route) ? '#ffffff' : '#6b7280',
                      backgroundColor: isActiveRoute(module.route) ? '#4f46e5' : openSubmenu === 'config' ? '#f3f4f6' : 'transparent',
                      cursor: 'pointer'
                    }}
                    onClick={() => setOpenSubmenu(openSubmenu === 'config' ? null : 'config')}
                  >
                    <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                      {getIcon(module.icon)}
                      {!showSidebar && <span>Configuración de Sistema</span>}
                    </div>
                    {!showSidebar && (
                      <ChevronRight 
                        size={16} 
                        style={{ 
                          transform: openSubmenu === 'config' ? 'rotate(90deg)' : 'rotate(0)', 
                          transition: 'transform 0.2s' 
                        }} 
                      />
                    )}
                  </Nav.Link>
                  {!showSidebar && (
                    <Collapse in={openSubmenu === 'config'}>
                      <div className="ms-3">
                        {systemConfigSubmenu.map(item => (
                          <Nav.Link
                            key={item.id}
                            as={Link}
                            to={item.route}
                            className={`mb-1 rounded ${isActiveRoute(item.route) ? 'text-white' : ''}`}
                            style={{ 
                              padding: '8px 10px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              color: isActiveRoute(item.route) ? '#ffffff' : '#9ca3af',
                              backgroundColor: isActiveRoute(item.route) ? '#4f46e5' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            {getIcon(item.icon)}
                            <span>{item.name}</span>
                          </Nav.Link>
                        ))}
                      </div>
                    </Collapse>
                  )}
                </div>
              );
            }
            
            return (
              <Nav.Link
                key={module.id}
                as={Link}
                to={module.route}
                className={`mb-2 rounded ${isActiveRoute(module.route) ? 'text-white' : ''}`}
                style={{ 
                  padding: '10px',
                  textDecoration: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  color: isActiveRoute(module.route) ? '#ffffff' : '#6b7280',
                  backgroundColor: isActiveRoute(module.route) ? '#4f46e5' : 'transparent'
                }}
              >
                {getIcon(module.icon)}
                {!showSidebar && <span>{module.name}</span>}
              </Nav.Link>
            );
          })}
        </Nav>
      </div>

      {/* Sidebar Mobile */}
      <Offcanvas 
        show={showMobileSidebar} 
        onHide={() => setShowMobileSidebar(false)}
        style={{ backgroundColor: '#ffffff' }}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            <div className="d-flex align-items-center">
              <img 
                src="https://cdn.prod.website-files.com/6296605b1f3f0029f029976a/64d3e0b2e9b27088a519f38a_Recurso%201.png"
                alt="TotalDealer"
                style={{ width: '30px', height: '30px', marginRight: '10px' }}
              />
              <div>
                <h5 className="text-primary mb-0">TotalDealer</h5>
                {company && (
                  <small className="d-block" style={{ color: '#9ca3af' }}>{company.tradeName || company.name}</small>
                )}
              </div>
            </div>
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Nav className="flex-column">
            {modules.map((module: Module) => {
              if (module.route === '/admin/config') {
                return (
                  <div key={module.id}>
                    <Nav.Link
                      className={`mb-2 rounded d-flex align-items-center justify-content-between ${isActiveRoute(module.route) ? 'text-white' : ''}`}
                      style={{ 
                        padding: '10px',
                        textDecoration: 'none',
                        color: isActiveRoute(module.route) ? '#ffffff' : '#6b7280',
                        backgroundColor: isActiveRoute(module.route) ? '#4f46e5' : openSubmenu === 'config' ? '#f3f4f6' : 'transparent',
                        cursor: 'pointer'
                      }}
                      onClick={() => setOpenSubmenu(openSubmenu === 'config' ? null : 'config')}
                    >
                      <div className="d-flex align-items-center" style={{ gap: '10px' }}>
                        {getIcon(module.icon)}
                        <span>Configuración de Sistema</span>
                      </div>
                      <ChevronRight 
                        size={16} 
                        style={{ 
                          transform: openSubmenu === 'config' ? 'rotate(90deg)' : 'rotate(0)', 
                          transition: 'transform 0.2s' 
                        }} 
                      />
                    </Nav.Link>
                    <Collapse in={openSubmenu === 'config'}>
                      <div className="ms-3">
                        {systemConfigSubmenu.map(item => (
                          <Nav.Link
                            key={item.id}
                            as={Link}
                            to={item.route}
                            onClick={() => setShowMobileSidebar(false)}
                            className={`mb-1 rounded ${isActiveRoute(item.route) ? 'text-white' : ''}`}
                            style={{ 
                              padding: '8px 10px',
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              color: isActiveRoute(item.route) ? '#ffffff' : '#9ca3af',
                              backgroundColor: isActiveRoute(item.route) ? '#4f46e5' : 'transparent',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            {getIcon(item.icon)}
                            <span>{item.name}</span>
                          </Nav.Link>
                        ))}
                      </div>
                    </Collapse>
                  </div>
                );
              }
              
              return (
                <Nav.Link
                  key={module.id}
                  as={Link}
                  to={module.route}
                  onClick={() => setShowMobileSidebar(false)}
                  className={`mb-2 rounded ${isActiveRoute(module.route) ? 'text-white' : ''}`}
                  style={{ 
                    padding: '10px',
                    textDecoration: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    color: isActiveRoute(module.route) ? '#ffffff' : '#6b7280',
                    backgroundColor: isActiveRoute(module.route) ? '#4f46e5' : 'transparent'
                  }}
                >
                  {getIcon(module.icon)}
                  <span>{module.name}</span>
                </Nav.Link>
              );
            })}
          </Nav>
        </Offcanvas.Body>
      </Offcanvas>

      {/* Main Content */}
      <div className="flex-grow-1" style={{ marginLeft: showSidebar ? '70px' : '250px' }}>
        {/* Header */}
        <Navbar bg="white" className="shadow-sm px-3" style={{ height: '60px' }}>
          <Button
            variant="link"
            className="d-lg-none text-dark p-0 me-3"
            onClick={() => setShowMobileSidebar(true)}
          >
            <Menu size={24} />
          </Button>

          <div className="d-flex align-items-center me-auto">
            <div className="position-relative">
              <Search 
                size={20} 
                className="position-absolute" 
                style={{ left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}
              />
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Buscar..."
                style={{ width: '300px' }}
              />
            </div>
          </div>

          <Nav className="ms-auto d-flex align-items-center">
            {/* Notificaciones */}
            <Dropdown align="end" className="me-3">
              <Dropdown.Toggle variant="link" className="text-dark position-relative p-0">
                <Bell size={20} />
                <span 
                  className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger"
                  style={{ fontSize: '10px' }}
                >
                  3
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '300px' }}>
                <Dropdown.Header>Notificaciones</Dropdown.Header>
                <Dropdown.Item>
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1 small">Nueva empresa registrada</h6>
                      <p className="mb-0 text-muted small">Hace 5 minutos</p>
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Item>
                  <div className="d-flex align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="mb-1 small">Usuario actualizado</h6>
                      <p className="mb-0 text-muted small">Hace 1 hora</p>
                    </div>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item className="text-center text-primary">
                  Ver todas las notificaciones
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            {/* Perfil de Usuario */}
            <Dropdown align="end">
              <Dropdown.Toggle variant="link" className="text-dark d-flex align-items-center p-0">
                <div className="d-flex align-items-center">
                  <div 
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2"
                    style={{ width: '35px', height: '35px' }}
                  >
                    {user?.profile.firstName[0]}{user?.profile.lastName[0]}
                  </div>
                  <div className="text-start me-2 d-none d-md-block">
                    <div className="small fw-medium">{user?.profile.firstName} {user?.profile.lastName}</div>
                    <div className="text-muted" style={{ fontSize: '12px' }}>
                      {isSuperAdmin() ? 'Super Admin' : user?.roles[0]?.name}
                    </div>
                  </div>
                  <ChevronDown size={16} />
                </div>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={() => navigate('/profile')}>
                  <User size={16} className="me-2" />
                  Mi Perfil
                </Dropdown.Item>
                <Dropdown.Item onClick={() => navigate('/settings')}>
                  <Settings size={16} className="me-2" />
                  Configuración
                </Dropdown.Item>
                <Dropdown.Divider />
                <Dropdown.Item onClick={handleLogout} className="text-danger">
                  <LogOut size={16} className="me-2" />
                  Cerrar Sesión
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </Nav>
        </Navbar>

        {/* Page Content */}
        <div className="page-content" style={{ 
          padding: '30px',
          minHeight: 'calc(100vh - 70px)',
          background: '#f8f9fa'
        }}>
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default MainLayout;